import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { fmtPrice, cn } from '../../../../lib/utils';
import { manualOrderTypeLabel } from '../manualLabels';

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50 last:border-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

// ctx: { prepared } from /cancel/prepare. Two phases:
//  - confirm: order details + countdown + Cancel / Confirm Cancel
//  - result (after confirm, when `result` is set): message + optional "Exit Filled Position" (reuses the
//    existing Manual Exit modal via onExitFilled) + Close.
export default function ConfirmManualCancelOrderModal({ ctx, submitting = false, error = null, result = null, onConfirm, onCancel, onExitFilled, onExpire }) {
  const prepared = ctx?.prepared || null;
  const [nowMs, setNowMs] = useState(() => Date.now());

  const expiresMs = prepared?.expires_at ? Date.parse(prepared.expires_at) : null;
  useEffect(() => {
    if (result) return undefined; // stop the countdown once we have a result
    const id = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, [result]);
  useEffect(() => {
    if (!result && expiresMs != null && nowMs >= expiresMs) onExpire?.();
  }, [result, nowMs, expiresMs, onExpire]);

  if (!prepared) return null;
  const remainingSec = expiresMs != null ? Math.max(0, Math.ceil((expiresMs - nowMs) / 1000)) : null;
  const expired = remainingSec === 0;
  const isSell = String(prepared.side || '').toUpperCase() === 'SELL';
  const showExit = !!result && result.offer_exit === true && Number(result.filled_quantity) > 0 && result.watchlist_row_id != null;

  const confirmDisabled = submitting || expired;

  const body = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-txt-primary">Cancel Manual Order</h3>
        <span className="badge border text-2xs bg-bear/10 border-bear/30 text-bear">LIVE</span>
      </div>

      <div className="mb-4">
        <Field label="Symbol" value={prepared.display_symbol || prepared.symbol} />
        <Field label="Side" value={<span className={isSell ? 'text-bear' : 'text-bull'}>{prepared.side}</span>} />
        <Field label="Order Type" value={manualOrderTypeLabel(prepared.order_type)} />
        <Field label="Order Price" value={fmtPrice(prepared.order_price)} />
        <Field label="Order Qty" value={prepared.order_quantity} />
        <Field label="Filled Qty" value={prepared.filled_quantity} />
        <Field label="Pending Qty" value={prepared.pending_quantity} />
        <Field label="Status" value={prepared.local_status} />
        {!result && (
          <Field
            label="Confirm In"
            value={<span className={cn('font-medium', expired ? 'text-bear' : remainingSec <= 10 ? 'text-warn' : 'text-txt-primary')}>{remainingSec}s</span>}
          />
        )}
      </div>

      {result && (
        <div className="rounded-md border border-info/25 bg-info/5 px-3 py-2.5 mb-4">
          <p className="text-2xs font-mono text-txt-secondary break-words">
            {result.status === 'ALREADY_FILLED'
              ? 'Order is already filled.'
              : showExit
              ? 'Remaining pending quantity cancelled. Filled quantity exists.'
              : (result.message || 'Cancel processed.')}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-bear/40 bg-bear/10 px-3 py-2.5 mb-4">
          <p className="text-xs font-mono text-bear break-words">{error}</p>
        </div>
      )}

      {/* Mobile (<lg): sticky footer within the BottomSheet's scrollable body, matching
          the exit/signal confirm sheets' idiom. lg: resets restore the desktop layout. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        {result ? (
          <>
            <button type="button" onClick={onCancel} className="btn-ghost min-h-[48px] lg:min-h-0">Close</button>
            {showExit && (
              <button
                type="button"
                onClick={() => onExitFilled?.(result)}
                className="font-semibold text-sm px-4 py-2 rounded-md border transition-colors bg-bear/15 border-bear/40 text-bear hover:bg-bear/25 min-h-[48px] lg:min-h-0"
              >
                {result.status === 'ALREADY_FILLED' ? 'Exit Position' : 'Exit Filled Position'}
              </button>
            )}
          </>
        ) : (
          <>
            <button type="button" onClick={onCancel} disabled={submitting} className="btn-ghost disabled:opacity-50 min-h-[48px] lg:min-h-0">Cancel</button>
            <button
              type="button"
              onClick={() => { if (!confirmDisabled) onConfirm(); }}
              disabled={confirmDisabled}
              className="font-semibold text-sm px-4 py-2 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-warn/15 border-warn/40 text-warn hover:bg-warn/25 min-h-[48px] lg:min-h-0"
            >
              {submitting ? 'Cancelling…' : 'Confirm Cancel'}
            </button>
          </>
        )}
      </div>
    </>
  );

  return createPortal(
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. */}
      <BottomSheet open onClose={onCancel} title="">
        {body}
      </BottomSheet>
    </>,
    document.body,
  );
}
